import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Toast } from '../components/ui';

export type ToastKind = 'info' | 'error';
export type ToastItem = { id: string; message: string; kind: ToastKind };
export type ToastValue = {
  /** Success/informational toast: auto-dismisses after 5 s. */
  notify: (message: string) => void;
  /** Error toast: persists until dismissed (CC-1) and never auto-dismisses. */
  notifyError: (message: string) => void;
  /** Removes every toast — used by the shell on navigation (CC-1). */
  clearToasts: () => void;
};

const ToastContext = createContext<ToastValue | null>(null);

/** Auto-dismiss window for non-error toasts (CC-1: 5 seconds). */
const AUTO_DISMISS_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const remove = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (message: string, kind: ToastKind) => {
      setItems((current) => {
        // CC-1: no duplicate toasts — the same message appears once.
        if (current.some((item) => item.message === message)) return current;
        return [...current, { id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`, message, kind }];
      });
    },
    []
  );

  const notify = useCallback((message: string) => push(message, 'info'), [push]);
  const notifyError = useCallback((message: string) => push(message, 'error'), [push]);
  const clearToasts = useCallback(() => {
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
    setItems([]);
  }, []);

  // CC-1: 5 s auto-dismiss for info toasts; errors persist until dismissed.
  useEffect(() => {
    for (const item of items) {
      if (item.kind !== 'info' || timers.current.has(item.id)) continue;
      const timer = window.setTimeout(() => remove(item.id), AUTO_DISMISS_MS);
      timers.current.set(item.id, timer);
    }
  }, [items, remove]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ notify, notifyError, clearToasts }), [notify, notifyError, clearToasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications" data-testid="toast-region">
        {items.map((item) => (
          <Toast key={item.id} item={item} onClose={() => remove(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * CC-1: toasts never follow you across pages — every route change clears the
 * region. Lives INSIDE the router (it reads the location); the provider itself
 * stays router-free so it can wrap `<RouterProvider>`.
 */
export function ClearToastsOnNavigate(): null {
  const { clearToasts } = useToast();
  const location = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    clearToasts();
  }, [location.pathname, clearToasts]);
  return null;
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
}
